﻿import * as Util from './util.js';

/**
 * Adds two numbers together.
 *
 * @param {element} tabsContainer - The first number to add.
 * @param {Object} options - config.
 * @param {string} [options.tabSelector] - CSS selector for the tabs
 * @param {string} [options.tabpanelSelector] - CSS selector for the tabpanels
 * @example
 * 
 * // tabs DOM structure
 * // Dom path:
 * .tabs[role="tablist"]] > button#{tabId}[role="tab"][aria-selected="true"][aria-controls="{tabPanelId}"]
 * HTML Example:
 * <div class="tabs" role="tablist"><button role="tab" aria-selected="true" aria-controls="panel1" id="tab1" /></div>
 * 
 * // tabpanel DOM structure
 * // Dom path:
 * #{tabPanelId}[role="tabpanel"][aria-labelledby="{tabId}"]
 * HTML Example:
 * <div id="{tabPanelId}" role="tabpanel" aria-labelledby="{tabId}"></div>
 * 
 * // Example usage:
 * import { tabs } from from './tabs.js';
 * const tabsContainer = document.querySelector('.tabs');
 * const tabs = new tabs(tabsContainer, {
 *    tabSelector: '[role="tab"]',
 *   tabpanelSelector: '[role="tabpanel"]'
 * });
 * 
 */
function tabs(tabsContainer, options) {
    const settings = Object.assign({
        // Default settings.
        tabSelector: '[role="tab"]',
        tabPanelSelector: '[role="tabpanel"]',
        callback: undefined
        // No default settings yet.
    }, options);
    const tabs = {
        elem: tabsContainer,
        tabButtons: tabsContainer.querySelectorAll(settings.tabSelector),
        tabPanels: tabsContainer.querySelectorAll(settings.tabPanelSelector)
    };
    tabs.tabButtons.forEach((button) => {
        const li = button.closest('li');
        if (li && li.classList.contains('active')) {
            button.removeAttribute('tabindex');
        }

        button.addEventListener('click', (e) => {
            e.preventDefault();
            const target = button.getAttribute('aria-controls');
            tabSelected(tabs, button, target);
        });
        button.addEventListener('keydown', (e) => {
            let index = Array.prototype.indexOf.call(tabs.tabButtons, e.currentTarget);
            // Left and Up arrow keys move to previous tab.
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                index--;
                if (index < 0) {
                    index = tabs.tabButtons.length - 1;
                }
                tabs.tabButtons[index].focus();
                e.preventDefault();
            }
            // Right and Down arrow keys move to next tab.
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                index++;
                if (index >= tabs.tabButtons.length) {
                    index = 0;
                }
                tabs.tabButtons[index].focus();
                e.preventDefault();
            }
            // Home key moves to first tab.
            if (e.key === 'Home') {
                tabs.tabButtons[0].focus();
                e.preventDefault();
            }
            // End key moves to last tab.
            if (e.key === 'End') {
                tabs.tabButtons[tabs.tabButtons.length - 1].focus();
                e.preventDefault();
            }
            // Enter or Space key activates the focused tab.
            if (e.key === 'Enter' || e.key === ' ') {
                const target = e.currentTarget.getAttribute('aria-controls');
                tabSelected(tabs, e.currentTarget, target);
                e.preventDefault();
            }
        });
    });

    function activateTab(tab) {
        if (tab.closest('li')) {
            tab.closest('li').classList.add('active');
        }
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');
        tab.classList.add('active');
    }
    function deactivateTab(tab) {
        if (tab.closest('li')) {
            tab.closest('li').classList.remove('active');
        }
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
        tab.classList.remove('active');
    }
    function activatePanel(panel) {
        panel.removeAttribute('hidden');
        panel.classList.remove('inactive');
        panel.classList.add('active');
    }
    function deactivatePanel (panel, hide) {
        if (hide) hidePanel(panel);
        panel.classList.remove('active');
        panel.classList.add('inactive');
    }
    function hidePanel(panel) {
        panel.setAttribute('hidden', 'true');
    }

    function tabIsActive(tab) {
        const li = tab.closest('li');
        const liHasActiveClass = li ? li.classList.contains('active') : false;
        return liHasActiveClass || tab.getAttribute('aria-selected') == 'true';
    }

    function tabSelected(tabs, selectedTab, targetPanelId) {
        if (tabIsActive(selectedTab)) return;

        // Deactivate all tabs and hide all panels.
        tabs.tabButtons.forEach((tab) => {
            if (tab == selectedTab) {
                activateTab(tab)
            } else {
                deactivateTab(tab);
            }
        });

        // The current active panel
        const activePanel = tabs.elem.querySelector(`${settings.tabPanelSelector}.active`);

        
        // Sets active state on the selected tab-panel then calls executeCallback()
        const displaySelectedPanel = function () {
            const targetPanel = document.getElementById(targetPanelId);
            if (targetPanel) activatePanel(targetPanel);
            executeCallback(tabs, selectedTab, targetPanelId);
        }


        // Called when active panel exit animation ends:
        // - sets 'hidden' attribute on old panel element
        // - calls displaySelectedPanel() to show selected panel
        const handlePanelAnimationEnd = function (event) {
            const panel = event.currentTarget;
            if (event.animationName == panel.dataset.animationExit) {
                hidePanel(panel);
                //displaySelectedPanel();
            }
        }

        // If activePanel exists:
        // - attaches animationend listener to activePanel
        // - marks all panels inactive so the exit animation can run
        // If activePanel doesn't exist:
        // - hides all panels
        // - immediately runs displaySelectedPanel() to show the target.
        if (activePanel && activePanel.dataset.animationExit) {
            activePanel.addEventListener('animationend', handlePanelAnimationEnd, { once: true });

            tabs.tabPanels.forEach((panel) => {
                deactivatePanel(panel, false)
            });
            displaySelectedPanel();
        } else {
            tabs.tabPanels.forEach((panel) => {
                deactivatePanel(panel, true)
            });
            
            displaySelectedPanel();
        }
        
    }

    // Executes the function defined in settings.callback
    function executeCallback(tabs, selectedTab, targetPanelId) {
        if (typeof (settings.callback) == "function") {
            settings.callback(tabs, selectedTab, targetPanelId);
        }
    }

    return tabs;
}



export { tabs };